import { EN_KO, TAO_KOE } from '../libs/';
import UserCentrics from './usercentrics';

const AGENTFORCE_SERVICE = 'Salesforce Chat';
const AGENTFORCE_FAKE_TOGGLE_SELECTOR = '.agentforce_fakebutton';
const AGENTFORCE_REAL_TOGGLE_SELECTOR = '#embeddedMessagingConversationButton';
const AGENTFORCE_POPUP_ID = 'popup_customconsent_agentforce';

class AgentforceHandlerClass {
	getConfig() {
		return window.tao_chat_widget_config?.agentforce || {};
	}

	setHiddenPrechatFields() {
		const fields = this.getConfig().hidden_prechat_fields;

		if ( ! fields || ! window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields ) {
			return;
		}

		window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields( this.getHiddenPrechatFields( fields ) );
	}

	getHiddenPrechatFields( fields ) {
		return {
			...fields,
			evo_PreChatOriginURL: new URL( window.location.pathname || '/', document.baseURI ).toString(),
		};
	}

	loadWidget() {
		if ( this.promise ) {
			return this.promise;
		}

		const config = this.getConfig();

		this.promise = new Promise( ( resolve, reject ) => {
			const scriptEl = document.createElement( 'script' );
			scriptEl.type = 'text/javascript';
			scriptEl.src = config.script_url;
			scriptEl.onload = () => {
				try {
					window.embeddedservice_bootstrap.settings.language = config.language || 'de';
					window.addEventListener( 'onEmbeddedMessagingReady', () => {
						this.setHiddenPrechatFields();
					} );

					window.embeddedservice_bootstrap.init(
						config.id,
						config.name || 'WebChat_V2',
						config.url,
						{
							scrt2URL: config.scrt2_url,
						},
					);
					resolve( window.embeddedservice_bootstrap );
				} catch ( err ) {
					reject( err );
				}
			};
			scriptEl.onerror = reject;
			document.head.appendChild( scriptEl );
		} );

		return this.promise.then( ( bootstrap ) => {
			this.hideFakeToggle();
			return bootstrap;
		} );
	}

	waitForLaunchChat( bootstrap ) {
		return new Promise( ( resolve, reject ) => {
			const startTime = Date.now();
			const timeout = 10000;

			const checkLaunchChat = () => {
				if ( bootstrap?.utilAPI?.launchChat ) {
					resolve( bootstrap );
					return;
				}

				if ( Date.now() - startTime >= timeout ) {
					reject( new Error( 'Agentforce launchChat is not available.' ) );
					return;
				}

				setTimeout( checkLaunchChat, 100 );
			};

			checkLaunchChat();
		} );
	}

	waitForRealToggle() {
		return new Promise( ( resolve, reject ) => {
			const buttonEl = document.querySelector( AGENTFORCE_REAL_TOGGLE_SELECTOR );

			if ( buttonEl ) {
				resolve( buttonEl );
				return;
			}

			const observer = new MutationObserver( () => {
				const observedButtonEl = document.querySelector( AGENTFORCE_REAL_TOGGLE_SELECTOR );
				if ( observedButtonEl ) {
					clearTimeout( timeout );
					observer.disconnect();
					resolve( observedButtonEl );
				}
			} );

			const timeout = setTimeout( () => {
				observer.disconnect();
				reject( new Error( 'Agentforce conversation button is not available.' ) );
			}, 10000 );

			observer.observe( document.body, {
				childList: true,
				subtree: true,
			} );
		} );
	}

	showChat() {
		return this.loadWidget()
			.then( ( bootstrap ) => this.waitForRealToggle().then( () => bootstrap ) )
			.then( ( bootstrap ) => this.waitForLaunchChat( bootstrap ) )
			.then( ( bootstrap ) => Promise.resolve( bootstrap.utilAPI.launchChat() ).then( () => bootstrap ) );
	}

	onClick() {
		const popupEl = document.getElementById( AGENTFORCE_POPUP_ID );
		if ( this.hasConsent() ) {
			this.showChat().catch( ( err ) => {
				TAO_KOE.console.log( 'Error opening Agentforce: ', err );
			} );
		} else if ( popupEl ) {
			TAO_KOE.open_popup( popupEl ).then( ( service ) => {
				if ( AGENTFORCE_SERVICE === service && this.hasConsent() ) {
					this.showChat().catch( ( err ) => {
						TAO_KOE.console.log( 'Error opening Agentforce: ', err );
					} );
				}
			} );
		}
	}

	hasConsent() {
		if ( window.UC_UI ) {
			const service = window.UC_UI.getServicesBaseInfo().find( ( { name } ) => AGENTFORCE_SERVICE === name );
			if ( service && 'consent' in service ) {
				return service.consent.status;
			}
		}

		return false;
	}

	showFakeToggle() {
		const badgeEls = Array.from( document.querySelectorAll( AGENTFORCE_FAKE_TOGGLE_SELECTOR ) );
		badgeEls.forEach( ( badgeEl ) => {
			badgeEl.hidden = false;
			badgeEl.classList.remove( 'en_hidden' );
		} );
	}

	hideFakeToggle() {
		const badgeEls = Array.from( document.querySelectorAll( AGENTFORCE_FAKE_TOGGLE_SELECTOR ) );
		badgeEls.forEach( ( badgeEl ) => {
			badgeEl.classList.add( 'en_hidden' );
			badgeEl.hidden = true;
		} );
	}

	loadWidgetIfConsented() {
		if ( ! this.hasConsent() ) {
			return;
		}

		this.loadWidget().catch( ( err ) => {
			TAO_KOE.console.log( 'Error loading Agentforce: ', err );
		} );
	}

	initFakeToggle() {
		const popupEl = document.getElementById( AGENTFORCE_POPUP_ID );
		const badgeEls = Array.from( document.querySelectorAll( AGENTFORCE_FAKE_TOGGLE_SELECTOR ) );

		badgeEls.forEach( ( badgeEl ) => {
			badgeEl.addEventListener( 'click', ( ev ) => {
				ev.preventDefault();
				this.onClick();
			} );
		} );

		if ( popupEl ) {
			this.showFakeToggle();
		}
	}
}

const AgentforceHandler = new AgentforceHandlerClass();

TAO_KOE.ready( 'agentforce.js', () => {
	if ( EN_KO.functions.isMobileApp() && 'ReactNativeWebView' in window ) {
		return;
	}

	if ( ! AgentforceHandler.getConfig().script_url ) {
		return;
	}

	Promise.race( [
		UserCentrics.ready(),
		new Promise( ( resolve ) => setTimeout( () => resolve(), 3000 ) ),
	] ).then( () => {
		if ( AgentforceHandler.hasConsent() ) {
			AgentforceHandler.loadWidgetIfConsented();
		} else {
			AgentforceHandler.initFakeToggle();
		}
	} );

	window.addEventListener( 'UC_GENERAL', ( ev ) => {
		if ( ev.detail && ev.detail[ AGENTFORCE_SERVICE ] ) {
			AgentforceHandler.loadWidgetIfConsented();
		}
	} );
} );

window.KOE_Agentforce_Handler = AgentforceHandler;

export default AgentforceHandler;
